import React from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@/src/shadComponents/ui/card';
import { PaymentReportData } from './hooks/useActivationPaymentSettings';
import { format } from 'date-fns';

interface PaymentReportSummaryProps {
    reportData: PaymentReportData;
    dateRange?: { from: Date; to: Date };
}

export default function PaymentReportSummary({
    reportData,
    dateRange,
}: PaymentReportSummaryProps) {
    const formatCurrency = (cents: number) => {
        return `$${(cents / 100).toFixed(2)}`;
    };

    const calculateWeekOverWeekVariance = () => {
        if (reportData.paymentsPreviousWeek === 0) {
            return reportData.paymentsCurrentWeek > 0 ? 100 : 0;
        }
        return (
            ((reportData.paymentsCurrentWeek -
                reportData.paymentsPreviousWeek) /
                reportData.paymentsPreviousWeek) *
            100
        );
    };

    const weekOverWeekVariance = calculateWeekOverWeekVariance();
    const isPositiveVariance = weekOverWeekVariance >= 0;

    return (
        <div className="space-y-6">
            {/* Date Range Info */}
            {dateRange?.from && dateRange?.to && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-center">
                        <p className="text-sm font-medium text-blue-800">
                            Showing payment data from{' '}
                            {format(dateRange.from, 'MMM dd, yyyy')} to{' '}
                            {format(dateRange.to, 'MMM dd, yyyy')}
                        </p>
                    </div>
                </div>
            )}

            {/* Recent Activity */}
            <div>
                <h3 className="text-base font-semibold text-foreground mb-4">
                    Recent Activity
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">
                                Last 24 Hours
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold text-foreground">
                                {reportData.paymentsLast24h}
                            </div>
                            <div className="text-sm text-muted-foreground">
                                {formatCurrency(reportData.revenueLast24h || 0)}{' '}
                                revenue
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">
                                Current Week
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold text-foreground">
                                {reportData.paymentsCurrentWeek}
                            </div>
                            <div className="text-sm text-muted-foreground">
                                {formatCurrency(
                                    reportData.revenueCurrentWeek || 0,
                                )}{' '}
                                revenue
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">
                                Week over Week
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center space-x-2">
                                <div className="text-2xl font-bold text-foreground">
                                    {reportData.paymentsCurrentWeek} vs{' '}
                                    {reportData.paymentsPreviousWeek}
                                </div>
                            </div>
                            <div
                                className={`text-sm font-medium ${
                                    isPositiveVariance
                                        ? 'text-green-600'
                                        : 'text-red-600'
                                }`}
                            >
                                {isPositiveVariance ? '+' : ''}
                                {weekOverWeekVariance.toFixed(1)}% change
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Totals */}
            <div>
                <h3 className="text-base font-semibold text-foreground mb-4">
                    Totals
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">
                                Current Month
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold text-foreground">
                                {reportData.paymentsCurrentMonth}
                            </div>
                            <div className="text-sm text-muted-foreground">
                                {formatCurrency(
                                    reportData.revenueCurrentMonth || 0,
                                )}{' '}
                                revenue
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">
                                Lifetime Total
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold text-foreground">
                                {reportData.paymentsLifetime}
                            </div>
                            <div className="text-sm text-muted-foreground">
                                {formatCurrency(
                                    reportData.revenueLifetime || 0,
                                )}{' '}
                                revenue
                            </div>
                            {reportData.lastSessionDate && (
                                <div className="text-xs text-muted-foreground mt-1">
                                    Last session:{' '}
                                    {format(
                                        new Date(reportData.lastSessionDate),
                                        'MMM dd, yyyy',
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">
                                Session Status
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-1">
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Paid:</span>
                                    <span className="font-medium">
                                        {reportData.paymentsLifetime}
                                    </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">
                                        Claimed:
                                    </span>
                                    <span className="font-medium">
                                        {reportData.totalClaimed}
                                    </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">
                                        Invalidated:
                                    </span>
                                    <span className="font-medium">
                                        {reportData.totalInvalidated}
                                    </span>
                                </div>
                                <div className="flex justify-between text-sm border-t pt-1">
                                    <span className="text-muted-foreground">
                                        Total Sessions:
                                    </span>
                                    <span className="font-medium">
                                        {reportData.totalSessions}
                                    </span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
